import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Radio } from 'lucide-react';
import { useAdminGetBroadcasts } from '@/api/auth/admin/admin';
import { BroadcastResponseStatus } from '@/api/auth/missionImpactAuthAPI.schemas';
import { CreateBroadcastDialog } from '@/components/admin/broadcasts/CreateBroadcastDialog';

interface NotificationBroadcastsCardProps {
  templateId: string;
  templateEventName: string;
}

/**
 * Card component displaying broadcasts for a notification template
 */
export function NotificationBroadcastsCard({ templateId, templateEventName }: NotificationBroadcastsCardProps) {
  const navigate = useNavigate();

  const { data: broadcastsData, isLoading } = useAdminGetBroadcasts(
    { templateId },
    { query: { enabled: !!templateId } }
  );

  const broadcasts = broadcastsData?.data?.broadcasts ?? [];

  const getStatusBadge = (status?: BroadcastResponseStatus) => {
    switch (status) {
      case BroadcastResponseStatus.Draft:
        return <Badge variant="outline">Draft</Badge>;
      case BroadcastResponseStatus.Queued:
        return <Badge variant="secondary">Queued</Badge>;
      case BroadcastResponseStatus.InProgress:
        return <Badge className="bg-blue-500">In Progress</Badge>;
      case BroadcastResponseStatus.Completed:
        return <Badge className="bg-green-500">Completed</Badge>;
      case BroadcastResponseStatus.Failed:
        return <Badge variant="destructive">Failed</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Radio className="h-5 w-5" />
            Broadcasts
          </CardTitle>
          <CardDescription>
            Send this notification to multiple recipients
          </CardDescription>
        </div>
        <CreateBroadcastDialog templateId={templateId} templateEventName={templateEventName} />
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : broadcasts.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">
            No broadcasts have been created for this template yet.
          </div>
        ) : (
          <div className="space-y-2">
            {broadcasts.map((broadcast) => (
              <div
                key={broadcast.id}
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 cursor-pointer"
                onClick={() => navigate(`/dashboard/admin/broadcasts/${broadcast.id}`)}
              >
                <div className="flex items-center gap-4">
                  <div>
                    <div className="font-medium">{broadcast.name}</div>
                    <div className="text-sm text-muted-foreground">
                      Created {formatDate(broadcast.createdUtc)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {broadcast.totalRecipients != null && (
                    <span className="text-sm text-muted-foreground">
                      {broadcast.totalRecipients} recipients
                    </span>
                  )}
                  {getStatusBadge(broadcast.status)}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
